import React, { useMemo } from "react";
import { Stack } from "@altimateai/lego";
import { Button, Combobox, DebouncedInput, NativeSelect } from "@altimateai/lego";
import { useQuery } from '@tanstack/react-query';
import { getPromptTags } from "@/helpers/apis";
import { useUsers } from "@/helpers/hooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { PromptType } from "./types";
import { IconSearch, IconX } from "@tabler/icons-react";
import useUserGroups from "@/pages/settings/components/userGroups/useUserGroups";
import { useAppState } from "@/modules/app/useAppContext";

const PromptLibraryFilters: React.FC = () => {
  const { rbacEnabled } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { users } = useUsers();

  // Fetch teams using the hook
  const { groups: teams } = useUserGroups();
  const typeOptions = useMemo(
    () => [
      { label: "Saved Prompts", value: PromptType.SAVED },
      { label: "Default Prompts", value: PromptType.DEFAULT },
    ],
    []
  );

  // Fetch prompt tags
  const { data: tagsData } = useQuery({
    queryKey: ["promptTags"],
    queryFn: getPromptTags,
  });

  const tags = tagsData?.tags || [];

  // Get current filter values from URL params
  const currentType = searchParams.getAll("type");
  const currentTeams = searchParams.getAll("teams");
  const currentOwners = searchParams.getAll("owners");
  const currentTags = searchParams.getAll("tags");
  const currentSort = searchParams.get("sort") || "created_at";
  const currentSearch = searchParams.get("search") || "";

  // Team filter options
  const teamOptions = teams.map(team => ({
    label: team.name,
    value: team.id.toString(),
  }));

  // Owner filter options
  const ownerOptions = useMemo(
    () =>
      users?.map(user => {
        let name = "";
        if (user.first_name && user.last_name) {
          name = `${user.first_name} ${user.last_name}`;
        } else {
          name = user.email;
        }
        return {
          label: name,
          value: user.id.toString(),
        };
      }) || [],
    [users]
  );

  // Tag filter options
  const tagOptions = tags.map(tag => ({
    label: tag,
    value: tag,
  }));

  // Sort options
  const sortOptions = [
    { label: "Recently Added", value: "created_at" },
    { label: "Last Used", value: "last_used_at" },
    { label: "Last Updated", value: "updated_at" },
  ];

  const handleTypeChange = (values: string[]) => {
    updateSearchParamsByKey({ type: values });
  };

  const handleTeamsChange = (values: string[]) => {
    updateSearchParamsByKey({ teams: values });
  };

  const handleOwnersChange = (values: string[]) => {
    updateSearchParamsByKey({ owners: values });
  };

  const handleTagsChange = (values: string[]) => {
    updateSearchParamsByKey({ tags: values });
  };

  const handleSortChange = (value: string) => {
    updateSearchParamsByKey({ sort: value });
  };

  const handleSearchChange = (value: string) => {
    updateSearchParamsByKey({ search: value });
  };

  // Active filters for display
  const activeFilters = useMemo(() => {
    const filters = [];

    if (currentType.length > 0) {
      filters.push({
        label: `Type: ${currentType.map(t => typeOptions.find(o => o.value === t)?.label).join(", ")}`,
        searchKey: "type",
        onRemove: () => updateSearchParamsByKey({ type: undefined }),
      });
    }

    if (currentTeams.length > 0) {
      const teamLabels = currentTeams
        .map(team => {
          const teamOption = teamOptions.find(o => o.value === team);
          return teamOption ? teamOption.label : team;
        })
        .join(", ");
      filters.push({
        label: `Team: ${teamLabels}`,
        searchKey: "teams",
        onRemove: () => updateSearchParamsByKey({ teams: undefined }),
      });
    }

    if (currentOwners.length > 0) {
      const ownerLabels = currentOwners
        .map(owner => {
          const ownerOption = ownerOptions.find(o => o.value === owner);
          return ownerOption ? ownerOption.label : owner;
        })
        .join(", ");
      filters.push({
        label: `Owner: ${ownerLabels}`,
        searchKey: "owners",
        onRemove: () => updateSearchParamsByKey({ owners: undefined }),
      });
    }

    if (currentTags.length > 0) {
      const tagLabels = currentTags.join(", ");
      filters.push({
        label: `Tags: ${tagLabels}`,
        searchKey: "tags",
        onRemove: () => updateSearchParamsByKey({ tags: undefined }),
      });
    }

    return filters;
  }, [
    currentType,
    currentTeams,
    currentOwners,
    currentTags,
    typeOptions,
    updateSearchParamsByKey,
    teamOptions,
    ownerOptions,
  ]);

  return (
    <Stack direction="column" gap="md">
      {/* Filter Controls */}
      <Stack direction={"column"}>
        <Stack direction="row" align="center" gap="md" className="al-w-full">
          <Combobox
            buttonProps={{
              className: rbacEnabled ? "!al-w-1/4" : "!al-w-1/3",
            }}
            placeholder="Type: All"
            header="Type"
            value={currentType}
            options={typeOptions}
            onChange={handleTypeChange}
            multiSelect
            showApplyButton
            showClearButton
          />
          {rbacEnabled && (
            <Combobox
              buttonProps={{
                className: "!al-w-1/4",
              }}
              placeholder="Teams: All"
              header="Teams"
              value={currentTeams}
              options={teamOptions}
              onChange={handleTeamsChange}
              multiSelect
              showApplyButton
              showClearButton
            />
          )}
          <Combobox
            buttonProps={{
              className: rbacEnabled ? "!al-w-1/4" : "!al-w-1/3",
            }}
            placeholder="Owner: All"
            header="Owner"
            value={currentOwners}
            options={ownerOptions}
            onChange={handleOwnersChange}
            multiSelect
            showApplyButton
            showClearButton
          />
          <Combobox
            buttonProps={{
              className: rbacEnabled ? "!al-w-1/4" : "!al-w-1/3",
            }}
            placeholder="Tags: All"
            header="Tags"
            value={currentTags}
            options={tagOptions}
            onChange={handleTagsChange}
            multiSelect
            showApplyButton
            showClearButton
          />
        </Stack>
        <Stack className={"al-w-full"}>
          <DebouncedInput
            icon={IconSearch}
            placeholder={"Search for prompt"}
            debounceMs={500}
            className={"al-w-2/3"}
            value={currentSearch}
            onChange={handleSearchChange}
          />
          <NativeSelect
            showPlaceholder
            label="Sort by:"
            className={"al-w-1/3"}
            variant={"outline"}
            value={currentSort}
            onChange={handleSortChange}
            options={sortOptions}
            placeholder="Sort by"
            buttonProps={{ "aria-label": "Sort prompts" }}
          />
        </Stack>
      </Stack>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <Stack direction="row" align="center" gap="sm" className="al-flex-wrap">
          {activeFilters.map((filter, index) => (
            <Stack
              align="center"
              gap="sm"
              key={index}
              className="al-bg-gray-100 al-px-2 al-py-1 al-rounded-md al-text-sm"
            >
              <span>{filter.label}</span>
              <Button
                variant={"ghost"}
                size={"icon"}
                title={`Remove filter ${filter.label}`}
                onClick={filter.onRemove}
                className="al-text-gray-500 !al-h-auto !al-w-auto"
              >
                <IconX className={"al-size-4"} />
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default PromptLibraryFilters;
